import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Lock, Mail } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Tabs, tabPanelId, type TabOption } from '@/components/ui/Tabs';
import { env } from '@/config/env';
import { ROLE_HOME, isPathForRole } from '@/constants/roles';
import { ApiRequestError } from '@/lib/apiClient';
import { SERVER_ERROR_TYPE } from '@/lib/forms';
import { useAuthStore } from '../store/auth.store';
import {
  signInFormSchema,
  type SignInFormValues,
  type SignInMethod,
} from '../schemas/login.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

export interface DemoAccount {
  role: string;
  email: string;
  password: string;
}

export interface SignInFormProps {
  /** Hidden on the platform console, which has no self-service reset. */
  showForgotPassword?: boolean;
  /**
   * Seeded accounts to offer as one-click fill, from
   * `features/auth/constants/demoAccounts`.
   *
   * Rendered only in development, so nobody has to open the seed file to walk
   * a console — and never in a production build.
   */
  demoAccounts?: DemoAccount[];
}

/** The two ways in. Both go to the same endpoint as one `identifier`. */
const SIGN_IN_TABS: readonly TabOption<SignInMethod>[] = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'MOBILE', label: 'Mobile number' },
];

/**
 * The sign-in form itself, shared by the institute and platform screens.
 *
 * There is one login endpoint and one set of rules; only the wording around the
 * form changes. Keeping the form here means a fix to the error handling or the
 * redirect applies to both screens at once (CLAUDE.md §3).
 */
export function SignInForm({ showForgotPassword = true, demoAccounts }: SignInFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((state) => state.login);

  /** Errors that belong to the form as a whole, not to one field. */
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(signInFormSchema),
    defaultValues: { method: 'EMAIL', identifier: '', password: '', rememberMe: false },
  });
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = form;
  useLiveFormErrors(form);
  const method = watch('method');
  /**
   * The box takes focus on arrival only. After a switch the focus belongs to
   * the tab just chosen — taking it would break arrow keys along the tablist.
   */
  const hasSwitched = useRef(false);

  /**
   * An email half-typed into the mobile box is not a mobile, so switching
   * starts the box empty rather than leaving the other kind's text to fail.
   */
  function changeMethod(next: SignInMethod) {
    if (next === method) return;
    hasSwitched.current = true;
    setValue('method', next);
    setValue('identifier', '');
    clearErrors('identifier');
    setFormError(null);
  }

  const onSubmit = handleSubmit(async ({ identifier, password, rememberMe }) => {
    setFormError(null);
    try {
      // `method` stays on the form: the server tells the two apart itself.
      const user = await login({ identifier, password, rememberMe });

      // Send them back where they were headed before the login redirect,
      // otherwise to the dashboard their role owns. The role decides, so
      // signing in on the "wrong" screen still lands the right person in the
      // right place.
      //
      // The intended path only wins if it is somewhere this role may actually
      // go. A stale `/student` tab that bounced an administrator here must not
      // decide where the administrator lands afterwards.
      const intended = (location.state as { from?: string } | null)?.from;
      const target =
        intended && isPathForRole(intended, user.role) ? intended : ROLE_HOME[user.role];
      navigate(target ?? '/', { replace: true });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        const fieldErrors = error.fieldMap;
        let matchedAField = false;
        for (const [field, message] of Object.entries(fieldErrors)) {
          if (field === 'identifier' || field === 'password') {
            setError(field, { type: SERVER_ERROR_TYPE, message });
            matchedAField = true;
          }
        }
        if (!matchedAField) setFormError(error.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <>
      {formError && (
        <Alert tone="critical" title="Could not sign you in">
          {formError}
        </Alert>
      )}

      {/* noValidate: our messages are friendlier than the browser's defaults. */}
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Tabs
          label="Sign in with"
          variant="segmented"
          options={SIGN_IN_TABS}
          value={method}
          onChange={changeMethod}
        />

        {/* Keyed by method so the box remounts empty. */}
        <div role="tabpanel" id={tabPanelId(method)} aria-labelledby={`${method}-tab`}>
          {method === 'EMAIL' ? (
            <Input
              key="EMAIL"
              label="Email address"
              type="email"
              placeholder="you@school.com"
              autoComplete="username"
              autoFocus={!hasSwitched.current}
              leftIcon={<Mail className="h-4 w-4" />}
              error={errors.identifier?.message}
              {...register('identifier')}
            />
          ) : (
            <PhoneInput
              key="MOBILE"
              label="Mobile number"
              placeholder="98765 43210"
              // `username`, not `tel-national`: a password manager pairs the saved
              // password with whatever box is marked as the account name.
              autoComplete="username"
              autoFocus={!hasSwitched.current}
              error={errors.identifier?.message}
              {...register('identifier')}
            />
          )}
        </div>

        <Input
          label="Password"
          placeholder="Enter your password"
          autoComplete="current-password"
          isPassword
          leftIcon={<Lock className="h-4 w-4" />}
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Checkbox label="Keep me signed in" {...register('rememberMe')} />
          {showForgotPassword && (
            <Link
              to="/forgot-password"
              className="text-xs sm:text-sm font-[560] text-accent transition-colors hover:text-accent-2 hover:underline"
            >
              Forgot password?
            </Link>
          )}
        </div>

        <Button
          type="submit"
          size="lg"
          fullWidth
          isLoading={isSubmitting}
          rightIcon={<ArrowRight className="h-4 w-4" />}
          className="mt-1"
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      {/* Development only. The list itself is what keeps the passwords out of
          a production bundle — see `constants/demoAccounts` — and this check
          keeps the panel off the screen. */}
      {env.isDev && demoAccounts && demoAccounts.length > 0 && (
        <section
          aria-label="Demo accounts for development"
          className="card flex flex-col gap-2.5 rounded-[16px] border border-line bg-surface-2 p-3.5 sm:p-4"
        >
          <div className="flex items-center justify-between gap-2">
            {/* Not `.lbl`: that class writes its own dark colour at two-class
                specificity, so the accent beside it would only win in the light
                theme. */}
            <p className="text-xs font-extrabold tracking-[0.01em] text-accent">
              Demo accounts (development)
            </p>
            <span className="shrink-0 rounded-pill border border-line bg-surface px-2 py-0.5 text-2xs font-bold text-ink-3">
              One-click fill
            </span>
          </div>

          <ul className="flex flex-col gap-2">
            {demoAccounts.map((account) => (
              <li key={account.email}>
                <button
                  type="button"
                  onClick={() => {
                    setValue('method', 'EMAIL');
                    setValue('identifier', account.email, { shouldValidate: true });
                    setValue('password', account.password, { shouldValidate: true });
                  }}
                  className="group flex w-full items-center justify-between gap-2 rounded-[10px] border border-line bg-surface p-2.5 text-left transition-all hover:border-accent hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="shrink-0 rounded-pill border border-accent-line bg-accent-soft px-2 py-0.5 text-2xs font-extrabold text-accent-2">
                        {account.role}
                      </span>
                      <span className="truncate font-mono text-xs font-semibold text-ink transition-colors group-hover:text-accent">
                        {account.email}
                      </span>
                    </span>

                    {/* Printed as well as filled in: the reader often wants to
                        paste it somewhere else — a second browser, an API
                        client — not only into the two fields above. */}
                    <span className="truncate font-mono text-2xs text-ink-2">
                      {account.password}
                    </span>
                  </span>

                  <span className="shrink-0 text-2xs font-bold text-accent opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                    Use <span aria-hidden>→</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
